import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useI18n, pickLocalized } from "@/lib/i18n";
import { ExternalLink, LogOut } from "lucide-react";

const CATS = ["PRODUCT_IDENTITY", "MATERIALS", "ORIGIN", "REPAIR_CARE", "END_OF_LIFE", "FOOTPRINT", "CERTIFICATIONS"];

function useBrandRep() {
  const [state, setState] = useState({ ready: false, user: null, brand: null });
  useEffect(() => {
    // Cookie is httpOnly — /brand/me tells us whether we have a valid session.
    http.get("/brand/me")
      .then((r) => setState({ ready: true, user: r.data.user, brand: r.data.brand }))
      .catch(() => setState({ ready: true, user: null, brand: null }));
  }, []);
  return state;
}

export default function BrandDashboard() {
  const nav = useNavigate();
  const { t, locale } = useI18n();
  const { ready, user, brand } = useBrandRep();
  const [data, setData] = useState(null);
  const [submissionState, setSubmissionState] = useState({ public_url: "", category_hint: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => http.get("/brand/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line

  if (!ready) return <div className="bp-container py-24" />;
  if (!user) { nav("/brand/sign-in", { replace: true }); return null; }

  const logout = async () => {
    try { await http.post("/brand/auth/logout"); } catch (e) { /* best-effort */ }
    nav("/");
  };

  const submitLink = async (e) => {
    e.preventDefault();
    if (!submissionState.public_url.startsWith("http")) { toast.error("Please provide a public http(s) URL."); return; }
    setBusy(true);
    try {
      await http.post("/brand/submissions", submissionState);
      toast.success("Submitted for editor review");
      setSubmissionState({ public_url: "", category_hint: "", note: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Seo title={`${brand?.display_name || "Brand"} — dashboard`} canonicalPath="/brand/dashboard" locale={locale} />
      <div className="bp-container py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Brand dashboard</div>
            <h1 className="font-serif text-4xl mt-1" data-testid="brand-dash-title">{brand?.display_name}</h1>
            <p className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to={`/brands/${brand?.slug}`} className="text-sm bp-link flex items-center gap-1" data-testid="brand-view-public"><ExternalLink size={14} /> View public profile</Link>
            <button onClick={logout} className="text-sm underline flex items-center gap-1" data-testid="brand-logout"><LogOut size={14} /> Log out</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile summary */}
          <div className="lg:col-span-2 p-6 bg-white border" style={{ borderColor: "var(--bp-border)" }}>
            <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Your public profile</div>
            <ul className="space-y-2 text-sm">
              {CATS.map((cat) => {
                const it = data?.profile?.checklist?.find((c) => c.category === cat);
                if (!it) return null;
                return (
                  <li key={cat} className="flex justify-between border-b last:border-b-0 py-2" style={{ borderColor: "var(--bp-border)" }}>
                    <span>{t.checklist_categories[cat]}</span>
                    <span className={`text-xs uppercase font-mono ${it.status === "FOUND" ? "text-green-800" : it.status === "PARTIAL" ? "text-amber-700" : ""}`} style={{ color: it.status === "NOT_FOUND" ? "var(--bp-text-muted)" : undefined }}>{it.status}</span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-4 text-xs italic" style={{ color: "var(--bp-text-muted)" }}>
              Claiming this profile does not change any status. Editors update the checklist based on publicly verifiable information.
            </p>
          </div>

          {/* Go further — Tokenance */}
          <div className="p-6 bg-white border" style={{ borderColor: "var(--bp-border)", background: "var(--bp-bg-secondary)" }} data-testid="tokenance-panel">
            <div className="uppercase text-xs tracking-[0.3em] mb-2" style={{ color: "var(--bp-text-muted)" }}>Go further</div>
            <h3 className="font-serif text-2xl mb-2">Tokenance T-Dpp+</h3>
            <p className="text-sm mb-3" style={{ color: "var(--bp-text-secondary)" }}>
              Ready to move from public transparency to a verified, cryptographically-anchored Digital Product Passport?
              Tokenance provides an audited T-Dpp+ stack that integrates with your existing product data.
            </p>
            <p className="text-xs italic mb-4" style={{ color: "var(--bp-text-muted)" }}>
              This is a separate paid service. BrandPassport remains a free public index and continues to summarise only publicly available information.
            </p>
            <a href="https://tokenance.io/contact" target="_blank" rel="noopener noreferrer" className="inline-block px-4 py-2 border border-black text-sm" data-testid="tokenance-cta">Contact Tokenance →</a>
          </div>
        </div>

        {/* Submit public link */}
        <div className="mt-8 p-6 bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <h3 className="font-serif text-2xl mb-2">Submit a public link</h3>
          <p className="text-sm mb-4" style={{ color: "var(--bp-text-secondary)" }}>
            Publicly published something we may have missed? Send the URL below and our editors will review it.
          </p>
          <div className="mb-4 p-3 border-l-4" style={{ borderColor: "#B97700", background: "#FFF8E1" }} data-testid="private-docs-warning">
            <p className="text-sm">
              <b>Public links only.</b> Do not send private documents, unreleased reports, internal presentations, or NDA-covered material through this form. BrandPassport indexes publicly available information — anything you submit here should already be published on your public website or in a public regulatory filing.
            </p>
          </div>
          <form onSubmit={submitLink} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest">Public URL (required)</label>
              <Input required type="url" value={submissionState.public_url} onChange={(e) => setSubmissionState({ ...submissionState, public_url: e.target.value })} placeholder="https://yourbrand.com/sustainability/factory-list" data-testid="brand-sub-url" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest">Which category does this relate to?</label>
              <select
                value={submissionState.category_hint}
                onChange={(e) => setSubmissionState({ ...submissionState, category_hint: e.target.value })}
                className="w-full border p-2 bg-white"
                style={{ borderColor: "var(--bp-border)" }}
                data-testid="brand-sub-cat"
              >
                <option value="">— select —</option>
                {CATS.map((c) => <option key={c} value={c}>{t.checklist_categories[c]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest">Note (optional)</label>
              <Textarea value={submissionState.note} onChange={(e) => setSubmissionState({ ...submissionState, note: e.target.value })} data-testid="brand-sub-note" placeholder="Context for the editor — e.g., 'This replaces our 2024 supplier list'." />
            </div>
            <Button type="submit" disabled={busy} className="rounded-none" data-testid="brand-sub-submit">{busy ? "Submitting…" : "Submit link for review"}</Button>
          </form>
        </div>

        {/* Changelog */}
        <div className="mt-8">
          <h3 className="font-serif text-2xl mb-3">Your changelog</h3>
          <ul className="space-y-2 text-sm bg-white border p-6" style={{ borderColor: "var(--bp-border)" }}>
            {(data?.changelog || []).map((c) => (
              <li key={c.id} className="flex gap-3 items-baseline border-b last:border-b-0 py-2" style={{ borderColor: "var(--bp-border)" }}>
                <time className="font-mono text-xs" style={{ color: "var(--bp-text-muted)" }}>{(c.changed_at || "").slice(0, 10)}</time>
                <span className="uppercase text-xs tracking-widest">{c.change_type.replace(/_/g, " ")}</span>
                {c.public_note && <span style={{ color: "var(--bp-text-secondary)" }}>{pickLocalized(c.public_note, locale)}</span>}
              </li>
            ))}
            {(!data?.changelog || data.changelog.length === 0) && <li style={{ color: "var(--bp-text-muted)" }}>No entries yet.</li>}
          </ul>
        </div>

        {/* Submissions history */}
        <div className="mt-8">
          <h3 className="font-serif text-2xl mb-3">Your submitted links</h3>
          <ul className="space-y-2 text-sm bg-white border p-6" style={{ borderColor: "var(--bp-border)" }}>
            {(data?.submissions || []).map((s) => (
              <li key={s.id} className="flex flex-wrap gap-3 items-baseline border-b last:border-b-0 py-2" style={{ borderColor: "var(--bp-border)" }}>
                <time className="font-mono text-xs" style={{ color: "var(--bp-text-muted)" }}>{(s.submitted_at || "").slice(0, 10)}</time>
                <span className="uppercase text-xs tracking-widest">{s.status}</span>
                <span className="uppercase text-xs" style={{ color: "var(--bp-text-muted)" }}>{s.category_hint || "—"}</span>
                <a href={s.suggested_url} target="_blank" rel="noopener noreferrer" className="bp-link truncate">{s.suggested_url}</a>
              </li>
            ))}
            {(!data?.submissions || data.submissions.length === 0) && <li style={{ color: "var(--bp-text-muted)" }}>No submissions yet.</li>}
          </ul>
        </div>
      </div>
    </>
  );
}
